import React, { useEffect, useRef, useState } from 'react';
import SuccessToast from '../SuccessToast';
import VariablesWindow from '../dashboard/VariablesWindow';
import { cambiarCliente, listarRegistros, obtenerContacto } from './contactosApi';
import { AvatarContacto, CanalContacto } from './PiezasContacto';
import { detalleDeError, fechaLocal, nombreDe } from '../../utils/contactos';

function FichaContacto({ botId, chatId, isAdmin = false, onVolver, etiquetaVolver = 'Volver a contactos' }) {
  const [contacto, setContacto] = useState(null);
  const [errorCarga, setErrorCarga] = useState(null);
  const [registros, setRegistros] = useState(null);
  const [errorRegistros, setErrorRegistros] = useState(null);
  const [paginaRegistros, setPaginaRegistros] = useState(0);
  const [variablesAbiertas, setVariablesAbiertas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const claveRef = useRef(null);
  claveRef.current = `${botId}-${chatId}`;

  useEffect(() => () => {
    claveRef.current = null;
  }, []);

  useEffect(() => {
    let vivo = true;
    setContacto(null);
    setErrorCarga(null);
    setGuardando(false);
    obtenerContacto(botId, chatId).then((res) => {
      if (!vivo) return;
      if (!res.ok) {
        setErrorCarga(detalleDeError(res, 'No se pudo cargar el contacto'));
        return;
      }
      setContacto(res.data);
    });
    return () => {
      vivo = false;
    };
  }, [botId, chatId]);

  useEffect(() => {
    let vivo = true;
    setRegistros(null);
    setErrorRegistros(null);
    listarRegistros(botId, chatId, paginaRegistros).then((res) => {
      if (!vivo) return;
      if (!res.ok) {
        setErrorRegistros(detalleDeError(res, 'No se pudieron cargar los registros'));
        setRegistros({ content: [] });
        return;
      }
      setRegistros(res.data);
    });
    return () => {
      vivo = false;
    };
  }, [botId, chatId, paginaRegistros]);

  const alternar = async () => {
    if (!contacto || guardando) return;
    const anterior = contacto.cliente;
    const clave = claveRef.current;
    setGuardando(true);
    setContacto((prev) => ({ ...prev, cliente: !anterior }));
    const res = await cambiarCliente(botId, chatId, !anterior);
    if (claveRef.current !== clave) return;
    setGuardando(false);
    if (!res.ok) {
      setContacto((prev) => ({ ...prev, cliente: anterior }));
      setToastExito(null);
      setToastError(detalleDeError(res, 'No se pudo cambiar'));
      return;
    }
    setContacto(res.data);
    setToastError(null);
    setToastExito(res.data.cliente ? 'Marcado como cliente' : 'Ya no es cliente');
  };

  return (
    <div className="contactos-ficha">
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      <button type="button" className="contactos-volver" onClick={onVolver}>
        <span aria-hidden="true">← </span>{etiquetaVolver}
      </button>
      {errorCarga ? <p className="vars-aviso">{errorCarga}</p> : null}
      {!errorCarga && !contacto ? <p className="config-card-hint">Cargando contacto…</p> : null}
      {contacto ? (
        <>
          <section className={`contactos-ficha-cabecera${contacto.cliente ? ' es-cliente' : ''}`}>
            <AvatarContacto contacto={contacto} grande />
            <div className="contactos-ficha-identidad">
              <h2>{nombreDe(contacto)}</h2>
              <CanalContacto plataforma={contacto.plataforma} />
            </div>
            <div className="contactos-ficha-acciones">
              <label className="contactos-interruptor">
                <input
                  type="checkbox"
                  role="switch"
                  className="contactos-switch"
                  aria-label="Cliente"
                  checked={contacto.cliente}
                  disabled={guardando}
                  onChange={alternar}
                />
                <span aria-hidden="true">{contacto.cliente ? 'Es cliente' : 'No es cliente'}</span>
              </label>
              <button type="button" className="admin-btn secondary" onClick={() => setVariablesAbiertas(true)}>
                Variables
              </button>
            </div>
          </section>
          <dl className="contactos-datos">
            <div>
              <dt>Teléfono</dt>
              <dd>{contacto.telefono || '—'}</dd>
            </div>
            <div>
              <dt>Usuario de WhatsApp</dt>
              <dd>{contacto.whatsappUsername ? `@${contacto.whatsappUsername}` : '—'}</dd>
            </div>
            <div>
              <dt>Último mensaje</dt>
              <dd>{fechaLocal(contacto.ultimoMensaje)}</dd>
            </div>
          </dl>
          <div className="contactos-seccion">
            <h3>Registros</h3>
            <span>Datos que el script ha guardado de esta persona</span>
          </div>
          {errorRegistros ? <p className="vars-aviso">{errorRegistros}</p> : null}
          {registros === null ? <p className="config-card-hint">Cargando registros…</p> : null}
          {registros && !errorRegistros && (registros.content ?? []).length === 0 ? (
            <p className="contactos-vacio contactos-vacio--suave">El script no ha guardado registros de este contacto</p>
          ) : null}
          {registros && (registros.content ?? []).length > 0 ? (
            <ol className="contactos-registros">
              {(registros.content ?? []).map((r) => (
                <li key={r.id}>
                  <div className="contactos-registro-cabecera">
                    <strong>{r.coleccion}</strong>
                    <time>{fechaLocal(r.creado)}</time>
                  </div>
                  <pre>{JSON.stringify(r.data, null, 2)}</pre>
                </li>
              ))}
            </ol>
          ) : null}
          {registros && (registros.hasPrevious || registros.hasNext) ? (
            <div className="contactos-paginacion">
              <button
                type="button"
                className="admin-btn secondary"
                disabled={!registros.hasPrevious}
                onClick={() => setPaginaRegistros((p) => Math.max(0, p - 1))}
              >
                Registros anteriores
              </button>
              <button
                type="button"
                className="admin-btn secondary"
                disabled={!registros.hasNext}
                onClick={() => setPaginaRegistros((p) => p + 1)}
              >
                Registros siguientes
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      {variablesAbiertas && contacto ? (
        <VariablesWindow
          botId={botId}
          chatId={chatId}
          titulo={nombreDe(contacto)}
          soloLectura={!isAdmin}
          onClose={() => setVariablesAbiertas(false)}
        />
      ) : null}
    </div>
  );
}

export default FichaContacto;
